import { type JSX, useEffect, useState } from 'react';
import type { Vergleichswert, Zielvergleich } from '@crm/kontrakte';
import { anzeigeVon } from '@crm/kontrakte/src/anzeige';

import {
  type Abfrageergebnis,
  type Berichtsergebnis,
  type Katalogkennzahl,
  berichtErgebnis,
  spanneText,
  tagText,
  veraenderungText,
  wertText,
} from '../api/berichte';
import { ApiFehler } from '../api/fehler';
import type { Groesse } from '../api/uebersicht';
import {
  AMPELWORT,
  achsenformat,
  kreisAus,
  kreisUntauglich,
  punkteAus,
  reihenAus,
  wirksameDarstellung,
  zeitraeumeText,
} from '../bericht/Ergebnis';
import { Kreisdiagramm } from '../komponenten/Diagramm';
import { Berichtsbild, spaltennamen } from './Berichtsbild';
import { useEinpassen, zeigt } from './einpassen';
import { FLACH_AB, Skelett, Veraenderungszeile, Widgetkarte } from './Widget';

/**
 * EIN BERICHT ALS WIDGET (Auftrag #27, #37, AUS-16, AUS-06).
 *
 * Kopfzeile mit Titel und Menü „Öffnen" wie jede Karte; darunter die
 * Hauptzahl — die Gesamtzeile der ersten Kennzahl — mit der Veränderung
 * gegen den ersten Vergleichszeitraum des Berichts, die Ziel-Ampel (AUS-11),
 * das Diagramm in der Darstellung des Berichts (auch als Kreis), ab 2×2 die
 * Legende oder die ersten Zeilen und unten der Stand der Daten (AUS-10).
 *
 * DAS FELD GANZ NUTZEN (Auftrag #38): Das Diagramm wächst mit dem Feld in
 * Breite und Höhe — Balken und Linie strecken sich, der Kreis wird so groß,
 * wie die kleinere Seite erlaubt. Die Legende daneben zeigt so viele Zeilen,
 * wie ganz passen, ohne Hinweis auf den Rest (Auftrag #41). In einem
 * flachen Feld (breiter als 2,2-mal die Höhe) stehen Zahl und Diagramm
 * nebeneinander. Gerollt wird nie; was nicht passt, fällt weg (einpassen.ts)
 * — die Zahl und das Diagramm bleiben. Auftrag CRM-143: Flach gehört die
 * Spalte daneben dem Diagramm allein; die Legende eines Balken- oder
 * Linienbilds steht links unter der Zahl, nur der Kreis behält sie neben sich.
 *
 * DAS SCHMALE FELD (Auftrag CRM-145, Telefon): Ist die Karte schmaler als
 * SCHMAL_BIS, steht nichts neben dem Diagramm — es nimmt die ganze Breite.
 * Ein Bild in Farbe (aufgeteilt, mehrere Kennzahlen, Kreis) erklärt seine
 * Farben in einer knappen Legende DARUNTER, mit Werten, wo sie passen, sonst
 * nur mit Namen. Bis zu WUNSCHFLAECHE hoch bleibt die Zeichenfläche vor den
 * nachrangigen Teilen; die Legende fällt eher weg als das Diagramm.
 *
 * GANZ ODER GAR NICHT (Auftrag CRM-145): Eine Legende mit Farbzeichen nennt
 * alle Reihen des Bilds oder fehlt — „Geschäft 208" allein neben drei Farben
 * ließe die übrigen ohne Erklärung. Gekürzt wird nur die Liste der ersten
 * Zeilen eines Berichts nach Kategorien; deren Namen stehen ohnehin an der
 * Achse.
 *
 * AN DEN BALKEN ABLESEN (Auftrag #40): Unter jedem Balken steht sein Monat,
 * darüber sein Wert (Berichtsbild.tsx). Über der Zeit gibt es deshalb keine
 * Werteliste daneben — das Diagramm nimmt die ganze Breite. Die Legende
 * bleibt für den Kreis und für Berichte nach Kategorien. Ohne Vergleichswert
 * steht keine Veränderung da (nie „vorher 0"), und der laufende Zeitraum ist
 * erklärt.
 *
 * Gerechnet wird mit den Rechten des BETRACHTERS über denselben Weg wie der
 * Bericht selbst (`/berichte/:id/ergebnis`). Fehlt ein Fachrecht, sagt das
 * Widget es. Ist der Bericht gelöscht oder nicht mehr geteilt, nimmt die API
 * ihn schon aus der Anordnung und der Arbeitsplatz zeigt den Hinweis; kommt
 * die Antwort hier trotzdem als 404 (gelöscht zwischen zwei Abrufen), steht
 * dasselbe im Widget.
 *
 * DIE ANZEIGE DES BERICHTS GILT AUCH HIER (Auftrag CRM-47): ohne Legende
 * keine Spalte daneben, ohne Zielwerte keine Ampel; Werte am Balken nach
 * Wahl — ohne Angabe wie bisher dort, wo keine Legende daneben steht. Was
 * das Widget nicht hat (Gitter, Achsentitel, Tabelle), schaltet nichts.
 *
 * ALLE KENNZAHLEN (Auftrag CRM-54): Ein Bericht mit mehreren Kennzahlen zeigt
 * oben die Gesamtwerte ALLER Kennzahlen untereinander, je mit dem Farbzeichen
 * ihrer Reihe — die Liste ist zugleich die Legende des Diagramms, eine zweite
 * Spalte daneben wiederholte sie nur. Das Diagramm zeichnet wie der Baukasten
 * eine Reihe je Kennzahl (`reihenAus`, rechte Achse als Linie); aufgeteilt je
 * Kennzahl und Wert eine, die Legende daneben nennt dann alle. Ein 1×1-Feld
 * zeigt nur die Gesamtwerte. Veränderung und Ziel-Ampel gelten wie bisher der
 * ersten Kennzahl und nennen sie deshalb beim Namen.
 *
 * VOLLSTÄNDIG WIE DAS ERGEBNIS (Auftrag CRM-60): Das Bild zeichnet die
 * Vergleiche der ersten Kennzahl nach denselben Regeln wie der Baukasten
 * (`reihenAus`: hellere Balken, gestrichelte und gepunktete Linien; nicht bei
 * Aufteilung und Kreis), dazu die Ziellinie. Je Vergleich steht eine
 * Veränderungszeile mit seinem Namen, und eine Zeile nennt Zeitraum und
 * Vergleichszeiträume („Mai–Okt 2026 · ggü. Mai–Okt 2025") — mit den
 * Farbzeichen ist sie die Legende der Vergleichsreihen. Fällt sie weg (zu
 * wenig Höhe) oder ist das Feld 1×1, zeichnet das Bild die Vergleiche nicht:
 * hellere Balken ohne Erklärung sagten nichts. Die Zahl, das Bild und die
 * erste Veränderung gehen vor; die weiteren Veränderungen danach. Der volle
 * Wortlaut der Zeiträume und die Hinweise des Servers (bis zum selben Tag,
 * Navision offen, abgeschnitten) stehen im Hinweis beim Überfahren.
 */
export function Berichtswidget({
  id,
  groesse,
  beiOeffnen,
}: {
  readonly id: string;
  readonly groesse: Groesse;
  readonly beiOeffnen: () => void;
}): JSX.Element {
  const [stand, setzeStand] = useState<Berichtsergebnis | 'weg' | 'fehler' | undefined>(undefined);
  /* Auftrag #38: nie rollen; in einem flachen Feld steht das Diagramm neben der Zahl. */
  const { ref, plan, flach, breite } = useEinpassen([stand], { flachAb: FLACH_AB });
  /* Auftrag CRM-145: gemessen am Rumpf, gemeint ist die Karte — sie ist um ihren Rand breiter. */
  const schmal = breite !== null && breite > 0 && breite + KARTENRAND < SCHMAL_BIS;

  useEffect(() => {
    let aktuell = true;
    void berichtErgebnis(id)
      .then((r) => {
        if (aktuell) setzeStand(r);
      })
      .catch((u: unknown) => {
        if (aktuell) setzeStand(u instanceof ApiFehler && u.status === 404 ? 'weg' : 'fehler');
      });
    return () => {
      aktuell = false;
    };
  }, [id]);

  if (stand === 'weg') {
    return (
      <Widgetkarte titel="Bericht" sinnbild="chart-bar">
        <p className="widget__leer" role="status">
          Dieser Bericht wurde gelöscht oder wird nicht mehr mit Ihnen geteilt.
        </p>
      </Widgetkarte>
    );
  }

  const r = typeof stand === 'object' ? stand : undefined;
  const e = r?.ergebnis ?? null;
  const k = e?.kennzahlen[0];
  const gesamt = e?.gesamt.werte[0];
  /* Auftrag CRM-54: mehrere Kennzahlen — alle zeigen, im 1×1-Feld nur ihre Gesamtwerte. */
  const mehrere = (e?.kennzahlen.length ?? 0) > 1;
  const nurGesamt = mehrere && groesse.breite < 2 && groesse.hoehe < 2;
  const kreis = r !== undefined && e !== null && r.bericht.beschreibung.darstellung === 'kreis' && kreisUntauglich(e) === null;
  const linie = r !== undefined && e !== null && wirksameDarstellung(r.bericht.beschreibung.darstellung, e) === 'linie';
  const gross = groesse.breite >= 2 && groesse.hoehe >= 2;
  /* Auftrag #40: Über der Zeit stehen Monat und Wert am Balken; eine Liste daneben wiederholte sie nur. */
  const zeitlich = !kreis && e?.gruppierung?.zeitlich === true;
  const laufend = !kreis ? e?.zeilen.filter((z) => z.unvollstaendig) ?? [] : [];
  const laufendText = `${linie ? 'Gestrichelt' : 'Schraffiert'}: ${laufend.map((z) => z.bezeichnung).join(', ')} läuft noch`;
  /*
   * Auftrag CRM-46: Ein aufgeteilter Bericht zeigt je Wert eine Reihe wie im
   * Baukasten — nebeneinander, gestapelt oder als Linien. Die Legende daneben
   * nennt die Werte mit ihrer Summe im Zeitraum, auch ueber der Zeit: Ohne sie
   * sagen die Farben nichts.
   */
  const darstellung = r === undefined || e === null ? 'balken' : wirksameDarstellung(r.bericht.beschreibung.darstellung, e);
  const anzeige = r === undefined ? undefined : anzeigeVon(r.bericht.beschreibung);
  const mitZielen = anzeige?.ziele !== false;
  const mitLegende = anzeige?.legende !== false;
  /*
   * Auftrag CRM-60: die Vergleiche im Bild wie im Ergebnis — nicht im 1×1-Feld,
   * und nur, solange die Zeitraumzeile steht, die sie erklärt.
   */
  const vergleicheMoeglich =
    e !== null
    && !kreis
    && e.aufteilung == null
    && e.vergleiche.length > 0
    && anzeige?.vergleiche !== false
    && !(groesse.breite < 2 && groesse.hoehe < 2);
  const vergleicheImBild = vergleicheMoeglich && zeigt(plan, 'zeitraum');
  const teile =
    e === null || kreis
      ? undefined
      : reihenAus(e, darstellung === 'linie' ? 'linie' : darstellung === 'gestapelt' ? 'gestapelt' : 'balken', {
          vergleiche: vergleicheImBild,
          ziele: mitZielen,
        });
  const mitZiellinie = teile?.reihen.some((rh) => rh.ton === 'ziel') === true;
  /* Ein Zeichen nur für eine Vergleichsreihe, die das Bild wirklich zeichnet — ohne Werte je Zeile gibt es keine. */
  const gezeichnet = (stufe: number): boolean =>
    mitLegende && teile?.reihen.some((rh) => rh.stufe === stufe && rh.werte.some((w) => w !== null)) === true;
  const t = e?.aufteilung ?? null;
  const einfach = !mehrere && t === null;

  const zeilen: readonly Legendenzeile[] = !gross || anzeige?.legende === false || e === null || k === undefined || (zeitlich && t === null)
    ? []
    : mehrere && !kreis
      ? t === null
        ? []
        : legendeAufgeteilt(e)
    : t !== null && !kreis
      ? [
          /*
           * Auftrag CRM-145: im Ton der Reihe wie `reihenAus` (`data-reihe`) — die
           * Farben der Kreisstücke wichen ab Reihe 2 vom Bild ab.
           */
          ...t.werte.slice(0, t.imBild).map((w, j) => ({
            schluessel: w.schluessel ?? '',
            name: w.bezeichnung,
            wert: wertText(e.gesamt.teile?.[j]?.[0]?.wert ?? null, k.einheit),
            farbe: null as number | null,
            ton: Math.min(j + 1, 10),
          })),
          ...(t.imBild < t.werte.length
            ? [{
                schluessel: '__uebrige',
                name: 'Übrige',
                wert: wertText(e.gesamt.uebrige?.[0]?.wert ?? null, k.einheit),
                farbe: null,
                ton: Math.min(t.imBild + 1, 10),
              }]
            : []),
        ]
    : kreis
      ? kreisAus(e).map((st, i) => ({
          schluessel: st.schluessel,
          name: st.beschriftung,
          wert: st.wertText,
          farbe: st.schluessel === '__uebrige' ? 8 : i + 1,
        }))
      : e.zeilen.map((z) => ({
          schluessel: z.schluessel ?? '',
          name: z.bezeichnung,
          wert: wertText(z.werte[0]?.wert ?? null, k.einheit),
          farbe: null as number | null,
        }));

  /*
   * Auftrag CRM-143: Flach steht die Legende eines Balken- oder Linienbilds
   * links unter der Zahl — die Spalte daneben gehört dem Diagramm allein.
   * Stand sie im Bild, teilte sie dessen Spalte noch einmal, und das
   * Diagramm bekam rund 40 % der Karte. Der Kreis behält sie neben sich.
   */
  const farbig = zeilen.some((z) => z.farbe !== null || z.ton !== undefined);
  /*
   * Auftrag CRM-145: Im schmalen Feld steht nichts neben dem Bild; eine
   * Legende in Farbe steht darunter, die Liste der ersten Zeilen entfällt.
   */
  const legendeUnten = schmal && !flach && farbig;
  const legendeLinks = flach && !kreis && zeilen.length > 0;
  const legendeImBild = zeilen.length > 0 && !legendeLinks && !schmal;
  /* Eine Legende in Farbe geht vor Stand und Hinweisen (Rang 1.8), die erste Zeilen-Liste bekommt den Rest. */
  const legendenrang = farbig ? '1.8' : '3';

  return (
    <Widgetkarte
      titel={r?.bericht.titel ?? 'Bericht'}
      sinnbild={kreis ? 'chart-pie' : linie ? 'chart-line-up' : 'chart-bar'}
      beiOeffnen={beiOeffnen}
      laedt={stand === undefined}
      klasse="widget--bericht"
      rumpf={ref}
      rumpfklasse={flach ? 'berichtswidget widget__rumpf--flach' : 'berichtswidget'}
    >
      {stand === undefined && (
        <>
          <Skelett art="zahl" />
          <Skelett art="verlauf" />
        </>
      )}
      {stand === 'fehler' && <p className="widget__leer">Das Ergebnis konnte nicht geladen werden.</p>}
      {r !== undefined && e !== null && k !== undefined && (
        <>
          {mehrere ? (
            <ul className="berichtswidget__gesamt" data-teil="kennzahl" data-rang="0" aria-label="Gesamtwerte">
              {e.kennzahlen.map((kz, i) => {
                const text = wertText(e.gesamt.werte[i]?.wert ?? null, kz.einheit);
                return (
                  <li key={String(i)} className="berichtswidget__gesamtzeile" title={`${kz.name}: ${text}`}>
                    {/* Das Farbzeichen der Reihe — nur, wo das Bild eine Reihe je Kennzahl zeichnet. */}
                    {!nurGesamt && t === null && (
                      <span className="legende__farbe legende__farbe--wert" data-reihe={String(Math.min(i + 1, 10))} aria-hidden="true" />
                    )}
                    <span className="berichtswidget__name">{kz.name}</span>
                    <span className="berichtswidget__gesamtwert">{text}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="widget__kennzahl" data-teil="kennzahl" data-rang="0">
              <span className="widget__zahl">{wertText(gesamt?.wert ?? null, k.einheit)}</span>
            </div>
          )}
          {/*
            Auftrag #26, CRM-60: je Vergleich des Berichts die Veränderung der ersten
            Kennzahl. Die erste geht mit dem Bild (Rang 1), die weiteren nach der
            Zeitraumzeile. Auftrag #40: Ohne Vergleichswert keine Zeile — „vorher 0"
            hieße, es seien null gewesen.
          */}
          {veraenderungen(e).map(({ j, wert, teil, rang }) =>
            zeigt(plan, teil) && (
              <div key={teil} className="widget__zeile" data-teil={teil} data-rang={rang}>
                <Veraenderungszeile
                  v={{
                    vorher: wert.wert!,
                    prozent: wert.veraenderung,
                    gegen: `${mehrere ? `${k.name} ` : ''}ggü. ${e.vergleiche[j]!.name}`,
                    besser: k.richtung === 'hoeher' ? 'mehr' : 'weniger',
                  }}
                  einheit={k.einheit === 'EUR' ? 'euro' : 'anzahl'}
                  punkte={k.aggregat === 'quote'}
                />
              </div>
            ),
          )}
          {!mehrere && !imTitel(k.name, r.bericht.titel) && zeigt(plan, 'name') && (
            <p className="widget__zusatz" title={k.name} data-teil="name" data-rang="2">
              {k.name}
            </p>
          )}
          {/* AUS-11 (Auftrag #29): die Ampel gegen den Zielwert der Gesamtzeile. */}
          {gesamt?.ziel?.ampel != null && mitZielen && zeigt(plan, 'ziel') && (
            <p
              className={`zielampel zielampel--${gesamt.ziel.ampel}`}
              data-teil="ziel"
              data-rang="1"
              title={zielhinweis(k.name, k.einheit, gesamt.ziel)}
            >
              {/* Auftrag CRM-60: das Zeichen der Ziellinie, wo das Bild sie zeichnet. */}
              {mitZiellinie && mitLegende && <span className="legende__linie legende__linie--ziel" aria-hidden="true" />}
              Ziel {mehrere ? `${k.name} ` : ''}
              {wertText(gesamt.ziel.ziel, k.einheit)}
              {gesamt.ziel.anteilig ? ' anteilig' : ''} · {AMPELWORT[gesamt.ziel.ampel]}
            </p>
          )}
          {/* Auftrag CRM-60: Zeitraum und Vergleichszeiträume benannt — mit den Zeichen die Legende der Vergleiche. */}
          {zeigt(plan, 'zeitraum') && (
            <p
              className="widget__zusatz berichtswidget__zeitraum"
              data-teil="zeitraum"
              data-rang="1.5"
              title={[zeitraeumeText(e), ...e.unvollstaendig.hinweise].join('\n')}
            >
              {(gezeichnet(1) || gezeichnet(2)) && <Reihenzeichen stufe={0} linie={linie} />}
              {zusammen(spanneText(e.zeitraum.von, e.zeitraum.bis))}
              {e.unvollstaendig.laufenderZeitraum && laufend.length === 0 ? ', läuft noch' : ''}
              {e.vergleiche.map((v, j) => (
                <span key={`${v.art}-${v.von}`}>
                  {' · '}
                  {e.vergleiche.length === 1 ? 'ggü. ' : `${v.name}: `}
                  {gezeichnet(j + 1) && <Reihenzeichen stufe={j === 0 ? 1 : 2} linie={linie} />}
                  {zusammen(spanneText(v.von, v.bis))}
                </span>
              ))}
            </p>
          )}
          {e.zeilen.length > 0 && !nurGesamt && zeigt(plan, 'bild') && (
            /*
              Auftrag #38: das Bild wächst in den Rest des Felds (data-mindest: so viel
              braucht es mindestens). Rang 1: Es geht vor Name und Stand, nicht vor der Zahl.
            */
            <div
              className={`berichtswidget__bild${legendeImBild ? ` berichtswidget__bild--mit-legende${kreis ? ' berichtswidget__bild--kreis' : ''}` : ''}`}
              data-teil="bild"
              data-rang="1"
              data-mindest="56"
              {...(schmal && !flach ? { 'data-wunsch': String(WUNSCHFLAECHE + RAND_DES_BILDS) } : {})}
              data-nebenan=""
            >
              <div className="berichtswidget__diagramm">
                {kreis ? (
                  <Kreisdiagramm klein stuecke={kreisAus(e)} beschriftung={r.bericht.titel} />
                ) : (
                  <Berichtsbild
                    art={linie ? 'linie' : 'balken'}
                    punkte={punkteAus(e)}
                    reihen={teile?.reihen}
                    gestapelt={teile?.gestapelt === true}
                    namen={spaltennamen(e.zeilen, e.gruppierung?.kennung ?? null)}
                    mitWerten={(anzeige?.werte ?? zeilen.length === 0) && (einfach || teile?.gestapelt === true)}
                    beschriftung={mehrere ? `${r.bericht.titel}: ${e.kennzahlen.map((kz) => kz.name).join(', ')}` : r.bericht.titel}
                    achse={achsenformat(k.einheit)}
                  />
                )}
              </div>
              {legendeImBild && <Legendenspalte zeilen={zeilen} kreis={kreis} ganz={farbig} />}
            </div>
          )}
          {/* Auftrag CRM-145: im schmalen Feld die Farben unter dem Bild — erst mit Werten, sonst nur die Namen. */}
          {legendeUnten && !nurGesamt && zeigt(plan, 'bild') && zeigt(plan, 'legende') && (
            <div className="berichtswidget__unten" data-teil="legende" data-rang={legendenrang} data-ganz="">
              <Legendenliste zeilen={zeilen} kreis={kreis} knapp />
            </div>
          )}
          {legendeUnten && !nurGesamt && zeigt(plan, 'bild') && zeigt(plan, 'legende-namen') && (
            <div className="berichtswidget__unten" data-teil="legende-namen" data-rang={legendenrang} data-ganz="" data-statt="legende">
              <Legendenliste zeilen={zeilen} kreis={kreis} knapp ohneWerte />
            </div>
          )}
          {/* AUS-10, Auftrag #40: was die Schraffur (gestrichelte Linie) bedeutet. */}
          {e.zeilen.length > 0 && laufend.length > 0 && zeigt(plan, 'laufend') && (
            <p className="widget__stand" title={laufendText} data-teil="laufend" data-rang="2">
              {laufendText}
            </p>
          )}
          {e.zeilen.length === 0 && zeigt(plan, 'leer') && (
            <p className="widget__leer" data-teil="leer" data-rang="1">
              Im Zeitraum des Berichts gibt es noch keine Daten.
            </p>
          )}
          {/* Auftrag CRM-143: flach links die Legende — sie bekommt, was unter Zahl und Zeilen übrig ist (Rang 3). */}
          {legendeLinks && zeigt(plan, 'legende') && (
            <div data-teil="legende" data-rang={legendenrang} {...(farbig ? { 'data-ganz': '' } : {})}>
              <Legendenliste zeilen={plan?.zeilen == null ? zeilen : zeilen.slice(0, plan.zeilen)} kreis={false} />
            </div>
          )}
          {/* AUS-10: der Stand, aus dem gelesen wurde. */}
          {zeigt(plan, 'stand') && (
            <p className="widget__stand" data-teil="stand" data-rang="2">
              Stand {STAND.format(new Date(e.stand))} Uhr
            </p>
          )}
        </>
      )}
      {r !== undefined && r.fehlend.length > 0 && zeigt(plan, 'fehlend') && (
        <p className="widget__leer" data-teil="fehlend" data-rang="1">
          Ohne Recht für {r.fehlend.map((f) => `„${f.name}"`).join(' und ')} — {e === null ? 'nichts zu zeigen' : 'nicht gezeigt'}.
        </p>
      )}
    </Widgetkarte>
  );
}

/**
 * Die Veränderungszeilen der ersten Kennzahl — nur, wo es einen Vergleichswert
 * gibt (Auftrag #40). Die erste gezeigte heißt `veraenderung` und hat Rang 1,
 * die weiteren kommen nach Bild und Zeitraum (Auftrag CRM-60).
 */
function veraenderungen(e: Abfrageergebnis): { j: number; wert: Vergleichswert; teil: string; rang: string }[] {
  const zeilen: { j: number; wert: Vergleichswert; teil: string; rang: string }[] = [];
  e.vergleiche.forEach((_, j) => {
    const wert = e.gesamt.werte[0]?.vergleiche[j];
    if (wert?.wert == null) return;
    const erste = zeilen.length === 0;
    zeilen.push({ j, wert, teil: erste ? 'veraenderung' : `veraenderung-${String(j + 1)}`, rang: erste ? '1' : '1.6' });
  });
  return zeilen;
}

/** Der Hinweis zur Ampel: Ziel, Abweichung und Toleranz wie in der Tabelle des Ergebnisses (CRM-60). */
function zielhinweis(name: string, einheit: Katalogkennzahl['einheit'], z: Zielvergleich): string {
  const abweichung =
    z.abweichung === null
      ? ''
      : ` · Abweichung ${Number(z.abweichung) > 0 ? '+' : ''}${wertText(z.abweichung, einheit)}${
          z.abweichungProzent === null ? '' : ` (${veraenderungText(z.abweichungProzent, false)})`
        }`;
  return `Ziel ${name}: ${wertText(z.ziel, einheit)}${z.anteilig ? ` anteilig bis ${tagText(z.bis)}` : ''}${abweichung} · Toleranz ${z.toleranz.replace('.', ',')} %`;
}

/** Eine Spanne bricht nicht in sich um („Jan–Sep 2026"), nur zwischen den Zeiträumen. */
function zusammen(text: string): string {
  return text.replace(/ /g, '\u00a0');
}

/** Das Zeichen einer Reihe der ersten Kennzahl — Zeitraum (Stufe 0) oder Vergleich (1, 2). */
function Reihenzeichen({ stufe, linie }: { readonly stufe: 0 | 1 | 2; readonly linie: boolean }): JSX.Element {
  return (
    <span
      className={
        linie
          ? `legende__linie legende__linie--stufe${String(stufe)}`
          : `legende__farbe legende__farbe--wert legende__farbe--stufe${String(stufe)}`
      }
      data-reihe="1"
      aria-hidden="true"
    />
  );
}

/** Auftrag #40: Die Unterzeile entfällt, wenn der Titel des Widgets die Kennzahl schon nennt. */
export function imTitel(name: string, titel: string): boolean {
  const n = name.trim().toLocaleLowerCase('de-DE');
  return n !== '' && titel.toLocaleLowerCase('de-DE').includes(n);
}

/** `farbe` ist ein Kreisstück (`legende__farbe--stueckN`), `ton` eine Reihe des Diagramms (`data-reihe`). */
type Legendenzeile = {
  readonly schluessel: string;
  readonly name: string;
  readonly wert: string;
  readonly farbe: number | null;
  readonly ton?: number;
};

/**
 * Auftrag CRM-54: die Legende eines aufgeteilten Berichts mit mehreren
 * Kennzahlen — je Kennzahl und Wert eine Zeile, in derselben Folge und
 * Farbe wie die Reihen von `reihenAus` (höchstens zehn Töne).
 */
export function legendeAufgeteilt(e: Abfrageergebnis): Legendenzeile[] {
  const t = e.aufteilung;
  if (t == null) return [];
  const zeilen: Legendenzeile[] = [];
  let ton = 0;
  e.kennzahlen.forEach((kz, i) => {
    const werte = [
      ...t.werte.slice(0, t.imBild).map((w, j) => ({ name: w.bezeichnung, wert: e.gesamt.teile?.[j]?.[i]?.wert ?? null })),
      ...(t.imBild < t.werte.length ? [{ name: 'Übrige', wert: e.gesamt.uebrige?.[i]?.wert ?? null }] : []),
    ];
    for (const w of werte) {
      ton = Math.min(ton + 1, 10);
      zeilen.push({
        schluessel: `${String(i)}|${w.name}`,
        name: `${kz.name} · ${w.name}`,
        wert: wertText(w.wert, kz.einheit),
        farbe: null,
        ton,
      });
    }
  });
  return zeilen;
}

/**
 * Die Legende oder die ersten Zeilen neben dem Diagramm (ab 2×2) — so viele,
 * wie in die Höhe des Bilds passen (Auftrag #38), ohne „+ N weitere" (#41).
 */
function Legendenspalte({ zeilen, kreis, ganz }: {
  readonly zeilen: readonly Legendenzeile[];
  readonly kreis: boolean;
  /** Auftrag CRM-145: eine Legende in Farbe — alle Zeilen oder keine. */
  readonly ganz: boolean;
}): JSX.Element {
  /* Ein fester Schlüssel und nicht das Feld selbst: `zeilen` entsteht bei jedem Zeichnen neu. */
  const { ref, plan } = useEinpassen([zeilen.map((z) => `${z.schluessel}=${z.wert}`).join('|')]);
  const gezeigt = plan?.zeilen == null ? zeilen : zeilen.slice(0, plan.zeilen);
  return (
    <div className="berichtswidget__legendenspalte" ref={ref}>
      {zeigt(plan, 'legende') && (
        <div data-teil="legende" data-rang="3" {...(ganz ? { 'data-ganz': '' } : {})}>
          <Legendenliste zeilen={gezeigt} kreis={kreis} />
        </div>
      )}
    </div>
  );
}

/**
 * Die Zeilen der Legende — neben dem Bild oder, flach, links unter der Zahl
 * (Auftrag CRM-143); `knapp` im schmalen Feld als Zeile unter dem Bild, die
 * umbricht (Auftrag CRM-145).
 */
function Legendenliste({ zeilen, kreis, knapp = false, ohneWerte = false }: {
  readonly zeilen: readonly Legendenzeile[];
  readonly kreis: boolean;
  readonly knapp?: boolean;
  readonly ohneWerte?: boolean;
}): JSX.Element {
  const farbig = zeilen.some((z) => z.farbe !== null || z.ton !== undefined);
  return (
    <ul
      className={`berichtswidget__legende${knapp ? ' berichtswidget__legende--knapp' : ''}`}
      aria-label={kreis || farbig ? 'Legende' : 'Die ersten Zeilen'}
    >
      {zeilen.map((z) => (
        <li key={z.schluessel} className="berichtswidget__zeile" data-zeile="" title={`${z.name}: ${z.wert}`}>
          {z.ton !== undefined ? (
            <span className="legende__farbe legende__farbe--wert" data-reihe={String(z.ton)} aria-hidden="true" />
          ) : (
            z.farbe !== null && <span className={`legende__farbe legende__farbe--stueck${String(z.farbe)}`} aria-hidden="true" />
          )}
          <span className="berichtswidget__name">{z.name}</span>
          {!ohneWerte && <span className="berichtswidget__wert">{z.wert}</span>}
        </li>
      ))}
    </ul>
  );
}

/*
 * Auftrag CRM-145: bis zu dieser Kartenbreite steht nichts neben dem Diagramm.
 * Telefone liegen hochkant bei 360 bis 440 px; das Tablet (ab 600 px) und der
 * Rechner (2×2 ab rund 500 px) bleiben darüber.
 */
export const SCHMAL_BIS = 480;
/** Der Innenrand der Karte links und rechts (`.widget`): Die Karte ist so viel breiter als ihr Rumpf. */
const KARTENRAND = 28;
/** Die Zeichenfläche, die das schmale Feld dem Diagramm vor Legende und Hinweisen freihält. */
const WUNSCHFLAECHE = 120;
/** Namen unter den Balken und das Band der Werte darüber (Berichtsbild.tsx). */
const RAND_DES_BILDS = 32;

const STAND = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});
